import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    Avatar,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    CircularProgress,
} from '@mui/material'
import {
    Description,
    CheckCircle,
    Cancel,
    HourglassEmpty,
    Visibility,
    Add,
    Logout,
    Person,
    Assignment,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { endUserApi } from '@api/endUser'
import { useAuthStore } from '@store/authStore'
import { useTranslation } from '@i18n'

const EndUserDashboard = () => {
    const { t, language } = useTranslation()
    const navigate = useNavigate()
    const { user, logout } = useAuthStore()
    const isRTL = language === 'ar'

    const { data: submissionsData, isLoading: loadingSubmissions } = useQuery({
        queryKey: ['mySubmissions'],
        queryFn: () => endUserApi.getMySubmissions(),
    })

    const { data: formsData, isLoading: loadingForms } = useQuery({
        queryKey: ['accessibleForms'],
        queryFn: () => endUserApi.getAccessibleForms(),
    })

    const submissions = submissionsData?.data || []
    const forms = formsData?.data || []

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success'
            case 'denied':
            case 'rejected': return 'error'
            case 'pending': return 'warning'
            default: return 'default'
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <CheckCircle color="success" />
            case 'denied':
            case 'rejected': return <Cancel color="error" />
            case 'pending': return <HourglassEmpty color="warning" />
            default: return <Description />
        }
    }

    const getStatusLabel = (status) => {
        const labels = {
            approved: isRTL ? 'موافق عليه' : 'Approved',
            denied: isRTL ? 'مرفوض' : 'Denied',
            rejected: isRTL ? 'مرفوض' : 'Rejected',
            pending: isRTL ? 'قيد المراجعة' : 'Pending',
        }
        return labels[status] || status
    }

    const handleLogout = () => {
        logout()
        navigate('/portal/login')
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
            <Container maxWidth="lg">
                {/* Header */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                                <Person fontSize="large" />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight={600}>
                                    {isRTL ? 'مرحباً' : 'Welcome'}, {user?.full_name || user?.name_en}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {user?.email}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Logout />}
                            onClick={handleLogout}
                        >
                            {isRTL ? 'تسجيل الخروج' : 'Logout'}
                        </Button>
                    </Box>
                </Paper>

                <Grid container spacing={3}>
                    {/* Available Forms */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                                {isRTL ? 'النماذج المتاحة' : 'Available Forms'}
                            </Typography>
                            <Divider sx={{ my: 2 }} />

                            {loadingForms ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : forms.length === 0 ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    {isRTL ? 'لا توجد نماذج متاحة' : 'No forms available'}
                                </Typography>
                            ) : (
                                <List>
                                    {forms.map((form) => (
                                        <Card key={form.id} sx={{ mb: 2 }}>
                                            <CardContent sx={{ pb: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={500}>
                                                    {isRTL ? form.title_ar : form.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {isRTL ? form.description_ar : form.description}
                                                </Typography>
                                            </CardContent>
                                            <CardActions>
                                                {form.can_submit && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<Add />}
                                                        onClick={() => navigate(`/form/${form.id}`)}
                                                    >
                                                        {isRTL ? 'تقديم طلب' : 'Submit'}
                                                    </Button>
                                                )}
                                            </CardActions>
                                        </Card>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>

                    {/* My Submissions */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                                {isRTL ? 'طلباتي' : 'My Submissions'}
                            </Typography>
                            <Divider sx={{ my: 2 }} />

                            {loadingSubmissions ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : submissions.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                    <Typography color="text.secondary">
                                        {isRTL ? 'لا توجد طلبات بعد' : 'No submissions yet'}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ mt: 2 }}
                                        onClick={() => forms.length > 0 && navigate(`/form/${forms[0].id}`)}
                                        disabled={forms.length === 0}
                                    >
                                        {isRTL ? 'تقديم طلب جديد' : 'Submit New Request'}
                                    </Button>
                                </Box>
                            ) : (
                                <List>
                                    {submissions.map((submission) => (
                                        <ListItem
                                            key={submission.id}
                                            sx={{
                                                mb: 1,
                                                bgcolor: 'background.paper',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                            }}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => navigate(`/portal/submission/${submission.id}`)}
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemAvatar>
                                                {getStatusIcon(submission.approval_status)}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle2">
                                                            {submission.submission_id}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={getStatusLabel(submission.approval_status)}
                                                            color={getStatusColor(submission.approval_status)}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="body2">
                                                            {isRTL ? submission.form_title_ar : submission.form_title}
                                                        </Typography>
                                                        <br />
                                                        <Typography component="span" variant="caption" color="text.secondary">
                                                            {new Date(submission.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                                                            {' • '}
                                                            {isRTL
                                                                ? `${submission.approved_count}/${submission.total_depts} موافقات`
                                                                : `${submission.approved_count}/${submission.total_depts} approvals`}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default EndUserDashboard
